import {
  Download,
  Trash2,
  Edit3,
  Share2,
  FolderPlus,
  ExternalLink,
  Shield,
} from "lucide-react";
import {
  PopoverMenu,
  MenuItem,
} from "../../components/common/ui/menu/PopoverMenu";

// Context menu-specific types
export type ContextMenuItem = MenuItem;

export interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

/**
 * Context menu for right-click interactions
 * Uses PopoverMenu with "cursor" alignment
 */
export function ContextMenu({ x, y, items, onClose }: ContextMenuProps) {
  // If no items, show "No actions available"
  const displayItems =
    items.length === 0
      ? [
          {
            label: "No actions available",
            onClick: () => {},
            disabled: true,
          },
        ]
      : items;

  return (
    <PopoverMenu
      x={x}
      y={y}
      items={displayItems}
      onClose={onClose}
      align="cursor"
    />
  );
}

/**
 * Generate context menu items based on selected node(s)
 */
export function getContextMenuItems(params: {
  selectedNodes: FileNode[];
  onRename?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  onViewDetails?: () => void;
  onNewFolder?: () => void;
  onChangePermission?: () => void;
  userRole?: string; // Organization role: admin, staff, client
  currentFolderPermission?: string; // Folder permission: none, read, modify, full
}): ContextMenuItem[] {
  const {
    selectedNodes,
    onRename,
    onDelete,
    onDownload,
    onShare,
    onViewDetails,
    onNewFolder,
    onChangePermission,
    userRole,
    currentFolderPermission = "full",
  } = params;

  const items: ContextMenuItem[] = [];

  // Determine if user can modify (upload/delete/rename)
  const isClient = userRole === "client";
  const canModify = isClient ? currentFolderPermission === "modify" : true; // Admin/staff can always modify

  // Determine if user is admin/staff (for permission management)
  const isAdminOrStaff = userRole === "admin" || userRole === "staff";

  // Everyone (admin/staff/client) can create folders
  const canCreateFolders = true;

  // If nothing selected, show "New Folder"
  if (selectedNodes.length === 0) {
    if (onNewFolder && canCreateFolders) {
      items.push({
        label: "New Folder",
        icon: <FolderPlus className="w-4 h-4" />,
        onClick: onNewFolder,
      });
    }
    return items;
  }

  const isSingleFile =
    selectedNodes.length === 1 && selectedNodes[0].type === "file";
  const isSingleFolder =
    selectedNodes.length === 1 && selectedNodes[0].type === "folder";
  const isSingleSubmission =
    selectedNodes.length === 1 && selectedNodes[0].type === "form_submission";
  const isMultiple = selectedNodes.length > 1;

  // View Submission (form submissions only)
  if (isSingleSubmission && onViewDetails) {
    items.push({
      label: "View Submission",
      icon: <ExternalLink className="w-4 h-4" />,
      onClick: onViewDetails,
    });
  }

  // Download (files only) - available to all users with access
  if ((isSingleFile || isMultiple) && onDownload) {
    items.push({
      label: isMultiple ? "Download All" : "Download",
      icon: <Download className="w-4 h-4" />,
      onClick: onDownload,
    });
  }

  // Rename (single item only, requires modify permission)
  // Submissions cannot be renamed
  // Clients can rename if they have modify permission
  if (!isMultiple && !isSingleSubmission && onRename && canModify) {
    items.push({
      label: "Rename",
      icon: <Edit3 className="w-4 h-4" />,
      onClick: onRename,
    });
  }

  // Change Permission (single folder only, admin/staff only)
  if (isSingleFolder && onChangePermission && isAdminOrStaff) {
    items.push({
      label: "Change Permission",
      icon: <Shield className="w-4 h-4" />,
      onClick: onChangePermission,
    });
  }

  // Share (single file or submission, requires modify permission or admin/staff)
  if (
    (isSingleFile || isSingleSubmission) &&
    onShare &&
    (canModify || !isClient)
  ) {
    items.push({
      label: "Share",
      icon: <Share2 className="w-4 h-4" />,
      onClick: onShare,
    });
  }

  // View Details (single file only) - available non clients
  if ((isSingleFile || isSingleSubmission) && onViewDetails && !isClient) {
    items.push({
      label: "View Details",
      icon: <ExternalLink className="w-4 h-4" />,
      onClick: onViewDetails,
    });
  }

  // Separator before dangerous actions
  if (items.length > 0 && onDelete && canModify) {
    items.push({ label: "", separator: true, onClick: () => {} });
  }

  // Delete (requires modify permission)
  // Clients can delete files and folders if they have modify permission
  if (onDelete && canModify) {
    items.push({
      label: isMultiple ? `Delete ${selectedNodes.length} items` : "Delete",
      icon: <Trash2 className="w-4 h-4" />,
      onClick: onDelete,
      danger: true,
    });
  }

  return items;
}

import { FileNode } from "./types";
